/**
 * CapabilityJobStatus - Shows job status for a specific capability
 *
 * Located in attacks/capabilities/ because this component is only used
 * by CapabilityDrawer (proper ownership).
 *
 * Uses shared ScanStatus components for unified UI across Asset,
 * Vulnerability, and Capability drawers.
 */
import { useMemo, useState } from 'react';

import {
  getScanStatusConfig,
  ScanStatusBox,
  ScanStatusModal,
} from '@/components/ScanStatus';
import { useCancelJob, useReRunJob } from '@/hooks/useJobs';
import { useCapabilityJobs } from '@/sections/attacks/capabilities/hooks';
import { downloadFile } from '@/sections/jobs/utils/downloadHelpers.util';

import { CapabilityJobStatusProps } from './types';

export function CapabilityJobStatus({ capability }: CapabilityJobStatusProps) {
  const [open, setOpen] = useState(false);
  const { mutateAsync: reRunJob } = useReRunJob();
  const { mutateAsync: cancelJob } = useCancelJob();

  const { jobs, status, isLoading, error, refetch } =
    useCapabilityJobs(capability);

  const statusConfig = useMemo(() => getScanStatusConfig(status), [status]);

  const actionHandlers = useMemo(
    () => ({
      handleRerun: (jobKey: string) => reRunJob({ jobKey }),
      handleCancel: (jobKey: string) => cancelJob({ jobKey }),
      handleDownload: (url: string, filename: string) =>
        downloadFile(url, filename),
    }),
    [reRunJob, cancelJob]
  );

  if (!capability) {
    return null;
  }

  return (
    <>
      <ScanStatusBox
        statusConfig={statusConfig}
        isLoading={isLoading}
        onViewDetails={() => setOpen(true)}
        disabled={!status}
      />
      <ScanStatusModal
        open={open}
        onClose={() => setOpen(false)}
        title={`${capability.name} Scans`}
        jobs={jobs}
        isLoading={isLoading}
        error={error}
        refetch={refetch}
        actionHandlers={actionHandlers}
      />
    </>
  );
}
